<script setup lang="ts">
import {
  computed,
} from 'vue';
import { useRoute } from 'vue-router';

import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
} from '@modules/affiliate-program-creatives/constants';
import {
  useAffiliateProgramCreativeGetQuery,
  useAffiliateProgramCreativeOptionsQuery,
} from '@modules/affiliate-program-creatives/submodules/edit/queries';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import {
  isString,
} from '@utils/guards';

import { useAffiliateProgramCreativeSave, useAffiliateProgramCreativeSchema } from './composables';

const route = useRoute();

const id = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data: optionsData, isLoading: isLoadingOptions } = useAffiliateProgramCreativeOptionsQuery();
const { data: creativeItem, isLoading: isLoadingItem } = useAffiliateProgramCreativeGetQuery(id);

const isLoading = computed(() => (
  isLoadingOptions.value || (!!id.value && isLoadingItem.value)
));

const {
  isEdit,
  formSchema,
  formGroups,
  selectedAffiliates,
  onFormChange,
  onFormInput,
} = useAffiliateProgramCreativeSchema(
  optionsData,
  creativeItem,
);

const {
  clearError,
  errors,
  isLoadingSave,
  onSubmit,
} = useAffiliateProgramCreativeSave(
  creativeItem,
  isEdit,
  selectedAffiliates,
);
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateProgramEditCreativePage'"
    type="form"
    :title="isEdit
      ? $getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'EDIT_TITLE')
      : $getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'CREATE_TITLE')
    "
  >
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="isEdit
          ? $getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'EDIT_SUBMIT')
          : $getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'CRATE_SUBMIT')
        "
        :custom-errors="errors"
        :groups="formGroups"
        :is-loading="isLoadingSave"
        @interacted="clearError"
        @input="onFormInput"
        @change="onFormChange"
        @submit="onSubmit"
      >
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'SETTINGS_BACK')"
            :to="{
              name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
              params: {
                type: 'link',
              },
            }"
            :loading="isLoadingSave"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
